import React, { useState, useEffect } from 'react'
import GridCard from 'employee-platform/shared/components/GridCard'
import { GridContainer } from 'employee-platform/shared/styles/Grid'
import { useSelector } from 'react-redux'
import { AppState } from 'redux/config/store'
import AllocationHistoryTable from './components/AllocationHistoryTable'
import { PreTaxProgramTypeMap } from 'employee-platform/shared/utils'
import BudgetBreakdown from 'employee-platform/shared/components/BudgetBreakdown'
import * as FlexIcons from 'core-system/Icons/Flex'
import palette from 'core-system/Themes/palette'
import EditAllocationModal from './components/EditAllocationModal'
import Button from 'core-system/Button'
import FlexContainer from 'core-system/FlexContainer'
import UpcomingAllocations from './components/UpcomingAllocations'
import Divider from 'core-system/Divider'
import {
  Alert as AlertIcon,
  Calendar as CalendarIcon,
} from 'core-system/Icons/Misc'
import { PreTaxProgram } from 'redux/employeePlatform/employeePlatformTypes'
import moment from 'moment'
import Text from 'core-system/Text'
import PreTaxInfo from './components/PreTaxInfo'
import { screenSizes } from 'shared/RandomUtils'
import useResizeListener from 'shared/Hooks/useResizeListener'
import {
  ORCA_PILOT_GROUP_NAME,
  SSA_COMPANY_ID,
} from 'redux/config/services/Constants'
import { useNavigate } from 'react-router-dom'
import { Locations } from 'shared/Router/Locations'

const getDaySuffix = (day: number) => {
  const mod100 = day % 100
  if (mod100 >= 11 && mod100 <= 13) return 'th'
  switch (day % 10) {
    case 1:
      return 'st'
    case 2:
      return 'nd'
    case 3:
      return 'rd'
    default:
      return 'th'
  }
}

const resolveEmployerDisplayName = (
  profileData: AppState['employeePlatform']['profileData']
) => {
  if (!profileData) return null

  const normalizeEmployerLabel = (raw: string | null | undefined) => {
    if (!raw) return null
    const trimmed = raw.trim()
    if (trimmed.length === 0) return null

    // Try to treat it like a URL first (handles foo.com, https://foo.com, foo.com/path)
    const withProto = /^https?:\/\//i.test(trimmed)
      ? trimmed
      : `https://${trimmed}`
    let host = trimmed
    try {
      host = new URL(withProto).hostname || trimmed
    } catch {
      host = trimmed
    }

    host = host.replace(/^www\./i, '')

    // If it's a domain, use the first label (movewithfleet.com -> movewithfleet)
    const firstLabel = host.split('.').filter(Boolean)[0] || host
    const label = firstLabel.trim()
    return label.length > 0 ? label : null
  }

  const direct =
    (profileData.employerName ?? null) ||
    (typeof profileData.employer === 'string' ? profileData.employer : null) ||
    (typeof profileData.employer === 'object' && profileData.employer
      ? profileData.employer.name
      : null)

  const directTrimmed = typeof direct === 'string' ? direct.trim() : ''
  const fromWebsite = normalizeEmployerLabel(
    (profileData as any).employerWebsite
  )
  const fromEmailDomain = normalizeEmployerLabel(
    typeof (profileData as any).email === 'string'
      ? ((profileData as any).email as string).split('@')[1]
      : null
  )

  const resolved =
    directTrimmed.length > 0 ? directTrimmed : fromWebsite || fromEmailDomain

  return resolved || null
}

const getContribDeadlineInfo = (
  preTaxPrograms: { [key: string]: PreTaxProgram },
  employerName?: string | null
) => {
  if (preTaxPrograms) {
    const daysToEOM = moment().endOf('month').diff(moment(), 'days')
    const currDayOfMonth = Number(moment().format('D'))
    // Get the first active program
    const activeProgramType = Object.keys(preTaxPrograms).find(
      (programType) => preTaxPrograms[programType] !== null
    )

    // If there are no active programs return nothing
    if (!activeProgramType) {
      return null
    }

    const normalizedEmployerName = (employerName ?? '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '')

    const isSuperhuman = normalizedEmployerName === 'superhuman'
    const isGrammarly = normalizedEmployerName === 'grammarly'
    // Local/dev testing override (avoid impacting production employers)
    const isMoveWithFleetTestEmployer =
      process.env.NODE_ENV !== 'production' &&
      normalizedEmployerName === 'movewithfleet'

    const contribDeadlineDay =
      isSuperhuman || isGrammarly || isMoveWithFleetTestEmployer
        ? 23
        : preTaxPrograms[activeProgramType]?.contributionDeadlineDay
    // If the employer has not set a contribution deadline day return the days to end of month
    if (!contribDeadlineDay) {
      return {
        deadlineDay: moment().endOf('month').format('D'),
        daysLeftToUpdate: daysToEOM,
        isSuperhuman: false,
      }
    }
    // If the current day is before the contribution deadline day return the number of days
    // until the deadline day, otherwise return the contribution deadline day + the days to EOM
    if (currDayOfMonth <= contribDeadlineDay) {
      return {
        deadlineDay: contribDeadlineDay,
        daysLeftToUpdate: contribDeadlineDay - currDayOfMonth,
        isSuperhuman,
      }
    } else {
      return {
        deadlineDay: contribDeadlineDay,
        daysLeftToUpdate: contribDeadlineDay + daysToEOM,
        isSuperhuman,
      }
    }
  }

  return null
}

const PreTaxOverviewView = React.memo(() => {
  const navigate = useNavigate()

  const { preTaxPrograms, profileData } = useSelector(
    (state: AppState) => state.employeePlatform
  )
  const { properties } = useSelector((state: AppState) => state.application)
  const { irsTaxLimit } = properties
  const employerName = resolveEmployerDisplayName(profileData)

  const segmentNames = profileData.segments.map((data) =>
    data.name.toLowerCase()
  )

  const hasORCA =
    segmentNames.includes(ORCA_PILOT_GROUP_NAME) &&
    profileData.employerId === SSA_COMPANY_ID

  const [isMobile, setIsMobile] = useState(
    window.innerWidth <= screenSizes.mobile
  )
  const handleResize = React.useCallback(
    (width: number) => {
      setIsMobile(width <= screenSizes.mobile)
    },
    [setIsMobile]
  )
  useResizeListener(() => handleResize(window.innerWidth), [])

  const [showEditAllocationModal, setShowEditAllocationModal] = useState(false)

  const preTaxBudgetItems = preTaxPrograms
    ? Object.keys(preTaxPrograms).map((programType) => {
        return {
          name: PreTaxProgramTypeMap[programType],
          cycleBalance: preTaxPrograms[programType]?.cycleBalance,
          balance: preTaxPrograms[programType]?.balance,
          icon:
            programType === 'COMMUTER_TRANSIT' ||
            programType === 'COMMUTER_ORCA' ? (
              <FlexIcons.Transit color={palette.primary.pink800} />
            ) : (
              <FlexIcons.Parking color={palette.primary.pink800} />
            ),
        }
      })
    : null

  const contribDeadlineInfo = getContribDeadlineInfo(
    preTaxPrograms,
    employerName
  )
  const programDescription =
    preTaxPrograms.COMMUTER_TRANSIT?.description ||
    preTaxPrograms.COMMUTER_PARKING?.description

  useEffect(() => {
    if (profileData?.finchEmploymentType === 'contractor') {
      navigate(Locations.EmployeeDashboard)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileData])

  const employerTransitMaxLimit =
    preTaxPrograms.COMMUTER_TRANSIT?.employerMaxAllocationLimit ?? irsTaxLimit
  const employerParkingMaxLimit =
    preTaxPrograms.COMMUTER_PARKING?.employerMaxAllocationLimit ?? irsTaxLimit

  const maxTransitAllocation =
    employerTransitMaxLimit -
    (preTaxPrograms.COMMUTER_TRANSIT?.employerContribution || 0) -
    (preTaxPrograms.COMMUTER_ORCA?.employeeDeduction?.employeeDeduction || 0)

  const maxParkingAllocation =
    employerParkingMaxLimit -
    (preTaxPrograms.COMMUTER_PARKING?.employerContribution || 0)

  const maxTransitAllocationORCA =
    employerTransitMaxLimit -
    (preTaxPrograms.COMMUTER_TRANSIT?.employerContribution || 0) -
    (preTaxPrograms.COMMUTER_TRANSIT?.employeeDeduction?.employeeDeduction || 0)

  return (
    <GridContainer numColumns={2}>
      <GridContainer numColumns={1}>
        <GridCard headerTitle='Current Cycle Balance'>
          {preTaxBudgetItems ? (
            <BudgetBreakdown
              budgetItems={
                hasORCA
                  ? preTaxBudgetItems
                  : preTaxBudgetItems.filter((item) => item.name !== 'ORCA')
              }
              summaryDirection='row'
              balanceKey='cycleBalance'
              iconBackgroundColor={palette.primary.pink200}
            />
          ) : null}
        </GridCard>
        <GridCard headerTitle='Upcoming Allocation'>
          <FlexContainer flexDirection='column'>
            {programDescription ? (
              <>
                <Text variant='action4' textColor={palette.text.secondary}>
                  {programDescription}
                </Text>
                <Divider margin='0.75rem 0' />
              </>
            ) : null}
            <UpcomingAllocations
              hasORCA={hasORCA}
              maxTransitAllocation={maxTransitAllocation}
              maxParkingAllocation={maxParkingAllocation}
              maxTransitAllocationORCA={maxTransitAllocationORCA}
            />
            <Divider margin='0.5rem 0' />
            {contribDeadlineInfo ? (
              <FlexContainer
                flexDirection='row'
                justifyContent='center'
                alignItems='center'
                marginBottom='1rem'
                marginTop='0.5rem'
              >
                {contribDeadlineInfo.daysLeftToUpdate > 3 ? (
                  <CalendarIcon
                    width='2.5rem'
                    height='2.5rem'
                    color={palette.text.secondary}
                  />
                ) : (
                  <AlertIcon
                    width='2rem'
                    height='2rem'
                    color={palette.text.secondary}
                  />
                )}
                <FlexContainer
                  flexDirection='column'
                  justifyContent='center'
                  marginLeft='0.5rem'
                >
                  <Text variant='h5'>
                    {`${contribDeadlineInfo.daysLeftToUpdate} day${
                      contribDeadlineInfo.daysLeftToUpdate === 1 ? '' : 's'
                    } left to update allocation`}
                  </Text>
                  <Text variant='action4' textColor={palette.text.placeholder}>
                    {`Allocation resets on the ${
                      contribDeadlineInfo.deadlineDay
                    }${getDaySuffix(
                      Number(contribDeadlineInfo.deadlineDay)
                    )} of each month`}
                  </Text>
                  {contribDeadlineInfo.isSuperhuman ? (
                    <Text variant='action4' textColor={palette.text.secondary}>
                      Note: Superhuman allocations must be updated by the 23rd.
                    </Text>
                  ) : null}
                </FlexContainer>
              </FlexContainer>
            ) : null}
          </FlexContainer>
          <Button onClick={() => setShowEditAllocationModal(true)}>
            Manage Your Allocation
          </Button>
        </GridCard>
        <GridCard headerTitle='Allocation History'>
          <AllocationHistoryTable isMobile={isMobile} />
        </GridCard>
      </GridContainer>
      <GridContainer numColumns={1} fitContentHeight>
        <GridCard headerTitle='Total Remaining Balance'>
          {preTaxBudgetItems ? (
            <BudgetBreakdown
              budgetItems={
                hasORCA
                  ? preTaxBudgetItems
                  : preTaxBudgetItems.filter((item) => item.name !== 'ORCA')
              }
              summaryDirection='row'
              balanceKey='balance'
              iconBackgroundColor={palette.primary.pink200}
            />
          ) : null}
        </GridCard>
        <GridCard headerTitle='Pre-Tax Commuter Benefits Info'>
          <PreTaxInfo />
        </GridCard>
      </GridContainer>
      {showEditAllocationModal ? (
        <EditAllocationModal
          closeModal={() => setShowEditAllocationModal(false)}
          open={showEditAllocationModal}
          isMobile={isMobile}
          hasORCA={hasORCA}
          maxTransitAllocation={maxTransitAllocation}
          maxParkingAllocation={maxParkingAllocation}
          maxTransitAllocationORCA={maxTransitAllocationORCA}
        />
      ) : null}
    </GridContainer>
  )
})

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  PreTaxOverviewView.displayName = 'PreTaxOverviewView'
}

export default PreTaxOverviewView
